/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { TokenConfig, TokenExtensions } from '../types/tokens';
import { Upload, Coins, Rocket, Shield, Eye, Settings, TrendingUp, Lock, ShieldCheck, EyeOff, Globe, Send, Loader2, FileText, ChevronDown, Info, HelpCircle, X, CheckCircle, Database, Zap, Beaker, Smile, Gamepad2, Landmark, Wallet, Image as ImageIcon, Plus, Brain, Server, MessageCircle, Percent, AlertCircle, Users, Wrench } from 'lucide-react';
import { uploadFileToPinata } from '../lib/pinata';
import { useDeploymentCalculator } from '../hooks/use-calculator';
import { mintTokenApi } from '../api/mint';
import { useWallet } from '../providers/WalletProvider';
import { submitTokenLaunch } from '../api/submit';
import { TREASURY_WALLET_SOL } from '../lib/constants';
import { bondingCurveDB } from '../lib/bonding-curve-db';
import { isValidSolanaAddress } from '../lib/utils/format';
import { solToLamports } from '../lib/utils/conversions';

interface TokenFormProps {
    onPreviewUpdate: (data: Partial<TokenConfig>) => void;
    onSuccess: (hash: string, ticker: string) => void;
    onConnectWallet: () => void;
    onGoToMarket?: (ticker: string) => void;
}

// Brand Icons
const XIcon = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
);

const DiscordIcon = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.419 0 1.334-.956 2.419-2.157 2.419zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.419 0 1.334-.946 2.419-2.157 2.419z"/>
    </svg>
);

const InfoModal = ({ onClose }: { onClose: () => void }) => (
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
        <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl p-6 md:p-8 border border-stone-200 relative overflow-y-auto max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-stone-100 rounded-full text-stone-400 hover:text-stone-900 transition-colors">
                <X size={24} />
            </button>
            
            <div className="mb-8">
                <h3 className="font-serif text-3xl font-bold text-stone-900 mb-2">How The Foundry Works</h3>
                <p className="text-stone-500">A decentralized foundry for next-gen digital assets.</p>
            </div>

            <div className="space-y-8">
                <div>
                    <h4 className="text-lg font-bold text-stone-900 flex items-center gap-2 mb-3">
                        <span className="w-6 h-6 bg-stone-900 text-white rounded-full flex items-center justify-center text-xs">1</span>
                        Optimized Configure & Mint
                    </h4>
                    <p className="text-sm text-stone-600 leading-relaxed pl-8">
                        Define your token's identity, supply, and advanced features (Token-2022). All metadata is automatically uploaded to decentralized storage (IPFS). The minting process creates the smart contract and mints the initial supply to your wallet.
                    </p>
                </div>

                <div>
                    <h4 className="text-lg font-bold text-stone-900 flex items-center gap-2 mb-3">
                        <span className="w-6 h-6 bg-stone-900 text-white rounded-full flex items-center justify-center text-xs">2</span>
                        Fair Launch Curve
                    </h4>
                    <p className="text-sm text-stone-600 leading-relaxed pl-8">
                        Instead of pre-sales, tokens can be launched on a <strong>Bonding Curve</strong>. This provides immediate liquidity. As users buy, the price increases deterministically.
                    </p>
                </div>

                <div>
                    <h4 className="text-lg font-bold text-stone-900 flex items-center gap-2 mb-3">
                        <span className="w-6 h-6 bg-stone-900 text-white rounded-full flex items-center justify-center text-xs">3</span>
                        Fees & Economics
                    </h4>
                    <div className="pl-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="bg-stone-50 p-4 rounded-xl border border-stone-200">
                            <div className="flex items-center gap-2 font-bold text-stone-700 text-sm mb-2"><Database size={14}/> Base Fee</div>
                            <div className="text-xs text-stone-500">0.1 SOL (Token) / 0.25 SOL (NFT) flat fee covers contract deployment, storage rent, and platform service.</div>
                        </div>
                        <div className="bg-stone-50 p-4 rounded-xl border border-stone-200">
                            <div className="flex items-center gap-2 font-bold text-stone-700 text-sm mb-2"><Zap size={14}/> Extensions</div>
                            <div className="text-xs text-stone-500 space-y-1">
                                <p>0.1 SOL per advanced feature.</p>
                                <ul className="list-disc list-inside pl-1 opacity-80">
                                    <li>Transfer Tax (Revenue)</li>
                                    <li>Fixed Supply (Revoke Mint)</li>
                                    <li>Immutable Metadata</li>
                                </ul>
                                <p className="mt-1">Fees go to the <span className="font-mono bg-stone-200 px-1 rounded" title={TREASURY_WALLET_SOL}>Treasury</span>.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-8 pt-6 border-t border-stone-100 flex justify-end">
                <button onClick={onClose} className="px-6 py-2.5 bg-stone-900 text-white rounded-lg font-bold text-sm hover:bg-nobel-gold hover:text-stone-900 transition-colors">
                    Got it
                </button>
            </div>
        </div>
    </div>
);

const USE_CASES = [
    { id: 'MEME', label: 'Meme', icon: Smile },
    { id: 'UTILITY', label: 'Utility', icon: Wrench },
    { id: 'COMMUNITY', label: 'Community', icon: Users },
    { id: 'DEFI', label: 'DeFi', icon: TrendingUp },
    { id: 'NFT', label: 'NFT', icon: ImageIcon },
    { id: 'AI', label: 'AI', icon: Brain },
    { id: 'INFRA', label: 'Infra', icon: Server },
    { id: 'META', label: 'Metaverse', icon: Gamepad2 },
    { id: 'RWA', label: 'RWA', icon: Landmark },
];

const Toggle = ({ checked, onChange, label, icon }: { checked: boolean; onChange: (checked: boolean) => void; label: string; icon: React.ReactNode }) => (
    <div className="flex items-center justify-between py-2">
        <label className="text-sm font-bold text-stone-700 flex items-center gap-2">{icon} {label}</label>
        <button
            type="button"
            onClick={() => onChange(!checked)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-stone-900' : 'bg-stone-200'}`}
        >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
    </div>
);

export const TokenForm: React.FC<TokenFormProps> = ({ onPreviewUpdate, onSuccess, onConnectWallet, onGoToMarket }) => {
    const { isConnected, chain, network, address, setNetwork } = useWallet();
    const [isLoading, setIsLoading] = useState(false);
    const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showTaxHelp, setShowTaxHelp] = useState(false);
    const [feeAuthError, setFeeAuthError] = useState<string | null>(null);
    const [useConnectedFeeAuth, setUseConnectedFeeAuth] = useState(false);
    
    // Progress Status State
    const [status, setStatus] = useState<'IDLE' | 'PROCESSING' | 'SUCCESS' | 'ERROR'>('IDLE');
    const [progress, setProgress] = useState(0);
    const [statusMessage, setStatusMessage] = useState('');
    
    const [useCase, setUseCase] = useState('MEME');
    const [galleryImages, setGalleryImages] = useState<string[]>([]);
    const [royaltyPercentage, setRoyaltyPercentage] = useState<string>("0");
    const [transferFeePercent, setTransferFeePercent] = useState<string>("0");

    const [config, setConfig] = useState<TokenConfig>({
        name: '',
        ticker: '',
        description: '',
        supply: 1000000000,
        decimals: 9,
        chain: chain || 'SOL',
        network: network || 'devnet',
        tokenStandard: 'SPL',
        tags: ['MEME'],
        extensions: {
            revokeMint: true,
            immutable: true,
            transferFeeBasisPoints: 0,
            interestRate: 0,
            feeWithdrawalAuthority: ''
        },
        royaltyBasisPoints: 0, // Default 0
        website: '',
        twitter: '',
        telegram: '',
        discord: '',
        whitepaper: ''
    });

    const isNft = useCase === 'NFT';
    const estimates = useDeploymentCalculator(config);
    const isMainnet = config.network === 'mainnet';

    const maxSupply = Number(18446744073709551615n / BigInt(Math.pow(10, config.decimals)));

    // Update config when royalty input changes
    useEffect(() => {
        const percentage = parseFloat(royaltyPercentage);
        if (!isNaN(percentage)) {
            // Convert % to Basis Points (1% = 100 bps)
            const bps = Math.floor(percentage * 100);
            setConfig(prev => ({ ...prev, royaltyBasisPoints: bps }));
        }
    }, [royaltyPercentage]);

    // Update config when transfer fee input changes
    useEffect(() => {
        const percentage = parseFloat(transferFeePercent);
        if (!isNaN(percentage) && percentage >= 0 && percentage <= 10) {
            const bps = Math.floor(percentage * 100);
            setConfig(prev => ({ 
                ...prev, 
                extensions: { ...prev.extensions, transferFeeBasisPoints: bps }
            }));
        }
    }, [transferFeePercent]);

    useEffect(() => {
        if (chain) {
            setConfig(prev => ({ 
                ...prev, 
                chain: chain, 
                tokenStandard: chain === 'SUI' ? 'SUI_COIN' : prev.tokenStandard === 'SUI_COIN' ? 'SPL' : prev.tokenStandard
            }));
        }
    }, [chain]);

    useEffect(() => {
        const isNftMode = useCase === 'NFT';
        setConfig(prev => ({ 
            ...prev, 
            tags: [useCase],
            supply: isNftMode ? 1 : (prev.supply === 1 ? 1000000000 : prev.supply),
            decimals: isNftMode ? 0 : (prev.decimals === 0 ? 9 : prev.decimals),
            tokenStandard: isNftMode ? 'SPL' : prev.tokenStandard
        }));
    }, [useCase]);

    useEffect(() => {
        onPreviewUpdate(config);
    }, [config, onPreviewUpdate]);

    useEffect(() => {
        if (useConnectedFeeAuth && address) {
            handleFeeAuthChange(address);
        } else if (!useConnectedFeeAuth && config.extensions?.feeWithdrawalAuthority === address) {
            handleFeeAuthChange('');
        }
    }, [address, useConnectedFeeAuth]);

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, field: 'imageUrl' | 'headerImageUrl') => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const localUrl = URL.createObjectURL(file);
            setConfig(prev => ({ ...prev, [field]: localUrl }));
            
            try {
                const url = await uploadFileToPinata(file);
                setConfig(prev => ({ ...prev, [field]: url }));
            } catch (err) {
                console.error("Upload failed", err);
            }
        }
    };

    const handleStandardChange = (standard: 'SPL' | 'TOKEN_2022') => {
        // Reset local transfer fee state if switching away/resetting
        setTransferFeePercent("0");
        
        setConfig(prev => ({
            ...prev,
            tokenStandard: standard,
            // Reset extensions to avoid carrying over invalid configs
            extensions: standard === 'SPL' ? {
                // For standard SPL, these are conceptual flags, not on-chain extensions.
                // We default to the most secure options.
                revokeMint: true,
                immutable: true,
            } : {
                // For Token-2022, extensions are opt-in and have costs. Default to off.
                transferFeeBasisPoints: 0,
                interestRate: 0,
                revokeMint: false,
                immutable: false,
                feeWithdrawalAuthority: prev.extensions?.feeWithdrawalAuthority || ''
            }
        }));
    };

    const handleExtensionChange = (key: keyof TokenExtensions, value: any) => {
        setConfig(prev => ({
            ...prev,
            extensions: { ...prev.extensions, [key]: value }
        }));
    };

    const handleFeeAuthChange = (val: string) => {
        handleExtensionChange('feeWithdrawalAuthority', val);
        if (val && !isValidSolanaAddress(val)) {
            setFeeAuthError("Invalid Solana address format");
        } else {
            setFeeAuthError(null);
        }
    };

    const handleSubmit = async () => {
        if (!isConnected) {
            onConnectWallet();
            return;
        }
        
        if (feeAuthError) {
            alert("Please correct the Fee Collection Wallet address.");
            return;
        }
        
        setIsLoading(true);
        setStatus('PROCESSING');
        setProgress(10);
        setStatusMessage('Initializing deployment...');

        // Progress simulation
        const timers: ReturnType<typeof setTimeout>[] = [];
        timers.push(setTimeout(() => { setProgress(30); setStatusMessage('Uploading metadata to IPFS...'); }, 1000));
        timers.push(setTimeout(() => { setProgress(50); setStatusMessage('Creating Mint Account...'); }, 2500));
        timers.push(setTimeout(() => { setProgress(75); setStatusMessage('Waiting for wallet signature...'); }, 4000));
        timers.push(setTimeout(() => { setProgress(85); setStatusMessage('Finalizing transaction...'); }, 5500));

        try {
            const result = await mintTokenApi({
                ...config,
                totalFee: estimates.total,
                userWalletAddress: address || '',
                isNft: isNft
            }, window.solana || window.suiWallet);

            timers.forEach(clearTimeout);

            if (result.success && result.data) {
                setProgress(100);
                setStatus('SUCCESS');
                setStatusMessage('Deployment Successful!');

                if (!isNft) {
                    bondingCurveDB.initializeCurve(result.data.mintAddress, config.chain);
                }
                await submitTokenLaunch(config, result.data);
                
                onSuccess(result.data.txHash, config.ticker);
                
                // Add short delay before navigation to allow user to see success state
                if (onGoToMarket && !isNft) {
                    setTimeout(() => onGoToMarket(config.ticker), 1500);
                }
            } else {
                setStatus('ERROR');
                setStatusMessage('Deployment Failed');
                alert("Minting failed: " + result.error);
            }
        } catch (e: any) {
            timers.forEach(clearTimeout);
            setStatus('ERROR');
            setStatusMessage('Error occurred');
            alert("Error: " + e.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-2xl border border-stone-200 shadow-soft-lg p-6 md:p-8 relative h-full flex flex-col">
            {showInfoModal && <InfoModal onClose={() => setShowInfoModal(false)} />}
            
            <div className="flex justify-end mb-4">
                <button 
                    onClick={() => setShowInfoModal(true)}
                    className="flex items-center gap-1.5 text-xs font-bold text-stone-400 hover:text-stone-900 transition-colors uppercase tracking-wide"
                >
                    <HelpCircle size={14} /> How it works
                </button>
            </div>

            <div className="space-y-6 flex-1 flex flex-col">
                <div className="flex-1 space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-stone-500 uppercase mb-2">{isNft ? "NFT Name" : "Token Name"}</label>
                                <input 
                                    type="text" 
                                    className="w-full p-3 bg-stone-50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-nobel-gold outline-none transition-all"
                                    placeholder={isNft ? "e.g. Bored Ape" : "e.g. My Token"}
                                    value={config.name}
                                    onChange={(e) => setConfig({ ...config, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-stone-500 uppercase mb-2">{isNft ? "Symbol (Optional)" : "Ticker Symbol"}</label>
                                <input 
                                    type="text" 
                                    className="w-full p-3 bg-stone-50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-nobel-gold outline-none transition-all font-mono"
                                    placeholder="e.g. THIS"
                                    value={config.ticker}
                                    onChange={(e) => setConfig({ ...config, ticker: e.target.value.toUpperCase() })}
                                />
                            </div>
                        </div>
                        
                        <div className="flex flex-col gap-4">
                            <div>
                                <label className="block text-xs font-bold text-stone-500 uppercase mb-2">{isNft ? "NFT Image" : "Token Icon"}</label>
                                <div className="relative h-[124px] w-full bg-stone-50 border-2 border-dashed border-stone-200 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-nobel-gold transition-colors group overflow-hidden">
                                    <input 
                                        type="file" 
                                        className="absolute inset-0 opacity-0 cursor-pointer z-10" 
                                        onChange={(e) => handleImageUpload(e, 'imageUrl')}
                                        accept="image/*"
                                    />
                                    {config.imageUrl ? (
                                        <img src={config.imageUrl} className="h-full w-full object-contain p-2" alt="Preview" />
                                    ) : (
                                        <>
                                            <div className="p-2 bg-white rounded-full shadow-sm mb-2 group-hover:scale-110 transition-transform">
                                                <Upload size={20} className="text-stone-400" />
                                            </div>
                                            <span className="text-xs text-stone-400 font-medium">Upload Image (PNG, JPG)</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-stone-500 uppercase mb-2">Description</label>
                        <textarea 
                            className="w-full p-3 bg-stone-50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-nobel-gold outline-none transition-all resize-none h-24"
                            placeholder="Describe your project..."
                            value={config.description}
                            onChange={(e) => setConfig({ ...config, description: e.target.value })}
                        />
                    </div>
                    
                    <div>
                        <label className="block text-xs font-bold text-stone-500 uppercase mb-2">Header Banner (1200x400 recommended)</label>
                        <div className="relative h-24 w-full bg-stone-50 border-2 border-dashed border-stone-200 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-nobel-gold transition-colors group overflow-hidden">
                            <input 
                                type="file" 
                                className="absolute inset-0 opacity-0 cursor-pointer z-10" 
                                onChange={(e) => handleImageUpload(e, 'headerImageUrl')}
                                accept="image/*"
                            />
                            {config.headerImageUrl ? (
                                <img src={config.headerImageUrl} className="h-full w-full object-cover" alt="Header Preview" />
                            ) : (
                                <>
                                    <div className="p-2 bg-white rounded-full shadow-sm mb-1 group-hover:scale-110 transition-transform">
                                        <Upload size={16} className="text-stone-400" />
                                    </div>
                                    <span className="text-xs text-stone-400 font-medium">Upload Banner</span>
                                </>
                            )}
                        </div>
                    </div>

                    {!isNft ? (
                        <div className="grid grid-cols-2 gap-6">
                            <div>
                                <label className="block text-xs font-bold text-stone-500 uppercase mb-2">Supply</label>
                                <input 
                                    type="number" 
                                    className="w-full p-3 bg-stone-50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-nobel-gold outline-none transition-all font-mono"
                                    value={config.supply}
                                    onChange={(e) => setConfig({ ...config, supply: Number(e.target.value) })}
                                    min="1"
                                    max={maxSupply}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-stone-500 uppercase mb-2">Decimals</label>
                                <input 
                                    type="number" 
                                    className="w-full p-3 bg-stone-50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-nobel-gold outline-none transition-all font-mono"
                                    value={config.decimals}
                                    onChange={(e) => setConfig({ ...config, decimals: Number(e.target.value) })}
                                    min="0"
                                    max="9"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="bg-stone-50 p-4 rounded-xl border border-stone-200">
                            <label className="block text-xs font-bold text-stone-500 uppercase mb-2 flex items-center gap-2">
                                <Percent size={14} /> Creator Royalties
                            </label>
                            <div className="flex items-center gap-3">
                                <div className="relative flex-1">
                                    <input 
                                        type="number" 
                                        className="w-full p-3 pr-8 bg-white border border-stone-200 rounded-xl focus:ring-2 focus:ring-nobel-gold outline-none transition-all font-mono"
                                        value={royaltyPercentage}
                                        onChange={(e) => setRoyaltyPercentage(e.target.value)}
                                        min="0"
                                        max="100"
                                        step="0.1"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm font-bold">%</span>
                                </div>
                                <div className="flex-1 text-xs text-stone-500">
                                    <p className="font-bold flex items-center gap-1 text-stone-700">
                                        = {Math.floor(parseFloat(royaltyPercentage || '0') * 100)} BPS
                                    </p>
                                    <p className="leading-tight mt-1">
                                        Basis Points: 500 = 5%. This is the commission you receive on secondary sales.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                     <div className="pt-4 border-t border-stone-100">
                        <button onClick={() => setIsAdvancedOpen(!isAdvancedOpen)} className="w-full flex justify-between items-center text-sm font-bold text-stone-600 hover:text-stone-900 transition-colors py-2 group">
                            <span className="flex items-center gap-2"><Settings size={16} className="text-stone-400 group-hover:text-stone-600 transition-colors"/> Advanced Options</span>
                            <ChevronDown size={18} className={`transition-transform text-stone-400 ${isAdvancedOpen ? 'rotate-180' : ''}`} />
                        </button>
                     </div>

                    {isAdvancedOpen && (
                        <div className="animate-fade-in space-y-6 pt-4 pb-2 border-t border-stone-100">
                             <div>
                                <label className="block text-xs font-bold text-stone-500 uppercase mb-2">Use Case</label>
                                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                                    {USE_CASES.map(uc => (
                                        <button 
                                            key={uc.id} 
                                            onClick={() => setUseCase(uc.id)}
                                            className={`p-2 rounded-lg border flex flex-col items-center justify-center gap-2 text-xs font-bold transition-all ${useCase === uc.id ? 'bg-stone-900 text-white border-stone-900' : 'bg-white border-stone-200 text-stone-500 hover:border-stone-400'}`}
                                        >
                                            <uc.icon size={16} />
                                            <span>{uc.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Socials & Links Section */}
                            <div>
                                <label className="block text-xs font-bold text-stone-500 uppercase mb-3">Project Socials</label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="relative">
                                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                                        <input 
                                            type="text" 
                                            className="w-full pl-9 p-2.5 bg-stone-50 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-nobel-gold outline-none transition-all placeholder:text-stone-400"
                                            placeholder="Website URL"
                                            value={config.website}
                                            onChange={(e) => setConfig({ ...config, website: e.target.value })}
                                        />
                                    </div>
                                    <div className="relative">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"><XIcon className="w-3.5 h-3.5" /></div>
                                        <input 
                                            type="text" 
                                            className="w-full pl-9 p-2.5 bg-stone-50 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-nobel-gold outline-none transition-all placeholder:text-stone-400"
                                            placeholder="X (Twitter) URL"
                                            value={config.twitter}
                                            onChange={(e) => setConfig({ ...config, twitter: e.target.value })}
                                        />
                                    </div>
                                    <div className="relative">
                                        <Send className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                                        <input 
                                            type="text" 
                                            className="w-full pl-9 p-2.5 bg-stone-50 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-nobel-gold outline-none transition-all placeholder:text-stone-400"
                                            placeholder="Telegram URL"
                                            value={config.telegram}
                                            onChange={(e) => setConfig({ ...config, telegram: e.target.value })}
                                        />
                                    </div>
                                    <div className="relative">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"><DiscordIcon className="w-3.5 h-3.5" /></div>
                                        <input 
                                            type="text" 
                                            className="w-full pl-9 p-2.5 bg-stone-50 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-nobel-gold outline-none transition-all placeholder:text-stone-400"
                                            placeholder="Discord URL"
                                            value={config.discord}
                                            onChange={(e) => setConfig({ ...config, discord: e.target.value })}
                                        />
                                    </div>
                                    <div className="relative sm:col-span-2">
                                        <FileText className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                                        <input 
                                            type="text" 
                                            className="w-full pl-9 p-2.5 bg-stone-50 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-nobel-gold outline-none transition-all placeholder:text-stone-400"
                                            placeholder="Whitepaper URL"
                                            value={config.whitepaper}
                                            onChange={(e) => setConfig({ ...config, whitepaper: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            {config.chain === 'SOL' && !isNft && (
                                <div>
                                    <label className="block text-xs font-bold text-stone-500 uppercase mb-2">Token Standard</label>
                                    <div className="grid grid-cols-2 gap-3">
                                        <button onClick={() => handleStandardChange('SPL')} className={`p-3 border-2 rounded-lg text-left ${config.tokenStandard === 'SPL' ? 'border-stone-900' : 'border-stone-200 hover:border-stone-400'}`}>
                                            <div className="font-bold text-sm text-stone-900">Standard SPL</div>
                                            <div className="text-xs text-stone-500">Basic, widely compatible.</div>
                                        </button>
                                        <button onClick={() => handleStandardChange('TOKEN_2022')} className={`p-3 border-2 rounded-lg text-left ${config.tokenStandard === 'TOKEN_2022' ? 'border-stone-900' : 'border-stone-200 hover:border-stone-400'}`}>
                                            <div className="font-bold text-sm text-stone-900">Token 2022</div>
                                            <div className="text-xs text-stone-500">Advanced features like tax.</div>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {config.tokenStandard === 'TOKEN_2022' && !isNft && (
                                <div className="p-4 bg-stone-50 rounded-xl border border-stone-200 space-y-4 animate-fade-in">
                                    <Toggle checked={!!config.extensions?.immutable} onChange={(c) => handleExtensionChange('immutable', c)} label="Immutable Metadata" icon={<ShieldCheck size={16}/>} />
                                    <Toggle checked={!!config.extensions?.revokeMint} onChange={(c) => handleExtensionChange('revokeMint', c)} label="Fixed Supply (Revoke Mint)" icon={<Lock size={16}/>} />
                                    
                                    <div className="border-t border-stone-200/60 pt-4">
                                         <div className="flex items-center justify-between">
                                            <label className="text-sm font-bold text-stone-700 flex items-center gap-2"><TrendingUp size={16}/> Transfer Fee (Tax)</label>
                                            <div className="flex items-center gap-2">
                                                <input 
                                                    type="number" 
                                                    value={transferFeePercent} 
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        const num = parseFloat(v);
                                                        if (v === '' || (num >= 0 && num <= 10)) {
                                                            setTransferFeePercent(v);
                                                        }
                                                    }} 
                                                    className="w-20 p-1 rounded-md text-right font-mono border border-stone-200" 
                                                    min="0" 
                                                    max="10"
                                                    step="0.01"
                                                />
                                                <span className="text-xs text-stone-500">%</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {(config.extensions?.transferFeeBasisPoints || 0) > 0 && (
                                        <div className="pl-6 pt-2 space-y-2">
                                            <label className="text-xs font-bold text-stone-500 uppercase flex items-center gap-2">Fee Collection Wallet</label>
                                            <input type="text" value={config.extensions?.feeWithdrawalAuthority || ''} onChange={(e) => handleFeeAuthChange(e.target.value)} className={`w-full p-2 rounded-md font-mono text-xs border ${feeAuthError ? 'border-red-400' : 'border-stone-200'}`} placeholder="Enter Solana Address" />
                                            {feeAuthError && <p className="text-xs text-red-500">{feeAuthError}</p>}
                                            <div className="flex items-center gap-2">
                                                <input type="checkbox" id="use-connected" checked={useConnectedFeeAuth} onChange={(e) => setUseConnectedFeeAuth(e.target.checked)} />
                                                <label htmlFor="use-connected" className="text-xs text-stone-600">Use Connected Wallet</label>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="pt-6 border-t border-stone-100 mt-auto space-y-6">
                    {isMainnet && (
                        <div className="p-4 bg-stone-50 rounded-xl border border-stone-200 space-y-2">
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-stone-500 font-bold">Platform Fee</span>
                                <span className="font-mono text-stone-700 font-bold">{estimates.base.toFixed(2)} SOL</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-stone-500 font-bold">Extension Fees</span>
                                <span className="font-mono text-stone-700 font-bold">{estimates.extensions.toFixed(2)} SOL</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-stone-500 font-bold">Solana Network Fees</span>
                                <span className="font-mono text-stone-700 font-bold">~{(estimates.storage + estimates.network).toFixed(6)} SOL</span>
                            </div>
                            <div className="pt-2 mt-2 border-t border-stone-200 flex justify-between items-center">
                                <span className="text-stone-800 font-bold text-sm">Total Cost</span>
                                <div className="text-right">
                                    <span className="font-mono text-stone-900 font-bold text-lg">{estimates.total.toFixed(4)} SOL</span>
                                    <span className="font-mono text-xs text-stone-500 block">~{solToLamports(estimates.total).toLocaleString()} Lamports</span>
                                </div>
                            </div>
                             <p className="text-[10px] text-stone-400 text-center pt-2">
                                Fees are transferred to the treasury: <span className="font-mono">{TREASURY_WALLET_SOL.slice(0, 8)}...</span>
                            </p>
                        </div>
                    )}

                    <button 
                        onClick={handleSubmit}
                        disabled={isLoading || !config.name || !config.ticker}
                        className="w-full py-4 bg-stone-900 text-white rounded-xl font-bold text-sm hover:bg-nobel-gold hover:text-stone-900 transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed group"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="animate-spin" size={18} /> {isNft ? 'Minting NFT...' : 'Deploying...'}
                            </>
                        ) : !isConnected ? (
                            <>
                                <Wallet size={18} /> Connect Wallet to Launch
                            </>
                        ) : (
                            <>
                                <Rocket size={18} className="group-hover:-translate-y-1 transition-transform" /> 
                                {isNft ? `Mint ${config.name || 'NFT'}` : `Launch ${config.ticker || 'Token'}`}
                            </>
                        )}
                    </button>

                    {status !== 'IDLE' && (
                        <div className="mt-4 bg-stone-50 border border-stone-200 rounded-xl p-3 animate-fade-in">
                            <div className="flex justify-between items-center text-xs font-bold text-stone-500 uppercase tracking-wide mb-2">
                                <span className={`flex items-center gap-2 ${status === 'ERROR' ? 'text-red-600' : status === 'SUCCESS' ? 'text-green-600' : 'text-stone-600'}`}>
                                    {status === 'PROCESSING' && <Loader2 size={12} className="animate-spin" />}
                                    {status === 'SUCCESS' && <CheckCircle size={12} />}
                                    {status === 'ERROR' && <AlertCircle size={12} />}
                                    {statusMessage}
                                </span>
                                <span>{Math.round(progress)}%</span>
                            </div>
                            <div className="h-1.5 w-full bg-stone-200 rounded-full overflow-hidden">
                                <div 
                                    className={`h-full transition-all duration-500 ease-out rounded-full ${
                                        status === 'ERROR' ? 'bg-red-500' : 
                                        status === 'SUCCESS' ? 'bg-green-500' : 'bg-nobel-gold'
                                    }`}
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};